import { useEffect, useRef, useState } from 'react';
import { AutoComplete, Input } from 'antd';
import { useGoogleMaps } from '../contexts/GoogleMapsContext';
import debounce from 'lodash/debounce';

interface Address {
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

interface Props {
  onSelect: (address: Address) => void;
  value?: string;
  onChange?: (value: string) => void;
  onAddressChange?: (value: string) => void;
  manualEntryAllowed?: boolean;
  skipAutocompleteOnChange?: boolean;
  countryRestriction?: string;
  status?: 'error' | 'warning' | '';
  placeholder?: string;
}

export default function PlacesAutocomplete({
  onSelect,
  value,
  onChange,
  onAddressChange,
  manualEntryAllowed = true,
  skipAutocompleteOnChange = false,
  countryRestriction,
  status,
  placeholder = "Voer een adres in",
}: Props) {
  const { isLoaded, loadError } = useGoogleMaps();
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | undefined>(undefined);
  const [options, setOptions] = useState<{ value: string; label: string; streetOnly: string }[]>([]);
  const [inputValue, setInputValue] = useState(value || '');
  const [isAutocompleteSelection, setIsAutocompleteSelection] = useState<boolean>(false);
  const [hasTypedWithoutSelection, setHasTypedWithoutSelection] = useState(false);

  // Keep internal state in sync with external value
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Log any load errors from the context
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps failed to load:', loadError);
    }
  }, [loadError]);

  useEffect(() => {
    if (isLoaded && window.google) {
      try {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        const mapDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(mapDiv);
        sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
      } catch (error) {
        console.error('Error initializing Places services:', error);
      }
    }
  }, [isLoaded]);

  // Create a debounced search function to avoid excessive API calls
  const debouncedSearch = useRef(
    debounce(async (searchText: string) => {
      if (!searchText || !autocompleteService.current || !sessionToken.current) {
        setOptions([]);
        return;
      }

      try {
        // Create request options
        const requestOptions: google.maps.places.AutocompletionRequest = {
          input: searchText,
          sessionToken: sessionToken.current,
          types: ['address'],
        };
        
        // Only add country restriction if specified
        if (countryRestriction) {
          requestOptions.componentRestrictions = { country: countryRestriction };
        }
        
        const results = await new Promise<google.maps.places.AutocompletePrediction[]>(
          (resolve, reject) => {
            autocompleteService.current?.getPlacePredictions(
              requestOptions,
              (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                  resolve([]);
                  return;
                }
                if (status !== google.maps.places.PlacesServiceStatus.OK) {
                  console.error('Places API error:', status);
                  reject(status);
                  return;
                }
                resolve(predictions || []);
              }
            );
          }
        );

        setOptions(
          results.map((result) => {
            // Extract just the street address from the full description
            const parts = result.description.split(',');
            const streetAddress = parts[0].trim();
            
            return {
              label: result.description, // Keep full address in dropdown
              value: result.description,
              streetOnly: streetAddress // Add street-only version
            };
          })
        );
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setOptions([]);
      }
    }, 300)
  ).current;

  const handleInputChange = (text: string) => {
    setInputValue(text);
    
    // Track if user is typing without selecting from suggestions
    if (text && !isAutocompleteSelection) {
      setHasTypedWithoutSelection(true);
    }
    
    // Notify parent component of address change
    if (onAddressChange) {
      onAddressChange(text);
    }
    
    // Notify parent component
    if (onChange) {
      onChange(text);
    }
    
    // Trigger search (debounced)
    debouncedSearch(text);
  };

  const handleSelect = async (selectedAddress: string, option: any) => {
    setIsAutocompleteSelection(true);
    setHasTypedWithoutSelection(false);
    
    // Update the input value immediately for better UX
    setInputValue(option.streetOnly || selectedAddress);
    
    // Notify parent of the change
    if (onChange) {
      onChange(option.streetOnly || selectedAddress);
    }

    if (!placesService.current || !autocompleteService.current) {
      console.error('Places service or autocomplete service not initialized');
      setIsAutocompleteSelection(false);
      return;
    }

    try {
      // Create request options
      const requestOptions: google.maps.places.AutocompletionRequest = {
        input: selectedAddress,
        sessionToken: sessionToken.current,
        types: ['address'],
      };
      
      // Only add country restriction if specified
      if (countryRestriction) {
        requestOptions.componentRestrictions = { country: countryRestriction };
      }
      
      const predictions = await new Promise<google.maps.places.AutocompletePrediction[]>(
        (resolve, reject) => {
          autocompleteService.current?.getPlacePredictions(
            requestOptions,
            (predictions, status) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK) {
                console.error('Failed to get place predictions:', status);
                reject(status);
                return;
              }
              resolve(predictions || []);
            }
          );
        }
      );

      if (!predictions.length) {
        console.error('No predictions found for:', selectedAddress);
        setIsAutocompleteSelection(false);
        return;
      }
      
      const placeId = predictions[0].place_id;

      const place = await new Promise<google.maps.places.PlaceResult>(
        (resolve, reject) => {
          placesService.current?.getDetails(
            {
              placeId,
              fields: ['address_components', 'formatted_address'],
              sessionToken: sessionToken.current,
            },
            (place, status) => {
              if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
                console.error('Failed to get place details:', status);
                reject(status);
                return;
              }
              resolve(place);
            }
          );
        }
      );

      const address: Address = {
        street: '',
        postalCode: '',
        city: '',
        country: '',
      };

      // Log the raw place data for debugging
      console.log('Raw place data:', place);

      place.address_components?.forEach((component) => {
        const types = component.types;

        if (types.includes('street_number')) {
          address.street = `${address.street} ${component.long_name}`.trim();
        }
        if (types.includes('route')) {
          address.street = `${component.long_name} ${address.street}`.trim();
        }
        if (types.includes('postal_code')) {
          address.postalCode = component.long_name;
        }
        if (types.includes('locality')) {
          address.city = component.long_name;
        }
        if (types.includes('country')) {
          address.country = component.long_name;
        }
      });

      // Ensure we have all address components before calling onSelect
      if (!address.street) {
        address.street = option.streetOnly || selectedAddress;
      }

      // Ensure postal code and city aren't empty
      if (!address.postalCode) {
        // Try to find postal code in the formatted address
        const postalCodeMatch = place.formatted_address?.match(/\b\d{4}\s?[A-Z]{2}\b/i);
        if (postalCodeMatch) {
          address.postalCode = postalCodeMatch[0];
        }
      }

      // Log the extracted address for debugging
      console.log('PlacesAutocomplete: extracted address', address);
      
      // Call onSelect directly
      onSelect(address);
      
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      // Reset after selection is processed
      setIsAutocompleteSelection(false);
    }
  };

  const handleBlur = () => {
    // Don't trigger address selection on blur if skipAutocompleteOnChange is true or if we're not allowing manual entry
    if (!manualEntryAllowed || skipAutocompleteOnChange) return;
    
    // Only trigger for manual entries, not for autocomplete selections
    if (isAutocompleteSelection) return;
    
    // Only process if we have a manual entry
    if (inputValue) {      
      const manualAddress: Address = {
        street: inputValue,
        postalCode: '',
        city: '',
        country: '', // Don't assume any country for manual entries
      };
      
      onSelect(manualAddress);
    }
  };

  if (!isLoaded) {
    return (
      <Input
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%' }}
        status={status}
      />
    );
  }

  return (
    <AutoComplete
      value={inputValue}
      options={options}
      onChange={handleInputChange}
      onSelect={handleSelect}
      onBlur={handleBlur}
      placeholder={placeholder}
      style={{ width: '100%' }}
      allowClear
      status={hasTypedWithoutSelection && skipAutocompleteOnChange ? 'warning' : status}
      notFoundContent={inputValue && options.length === 0 ? "Geen adressen gevonden. Probeer een ander adres." : null}
    />
  );
}
